import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import CardNB from '../../../components/ui-nb/CardNB';
import BadgeNB from '../../../components/ui-nb/BadgeNB';
import { useTranslation } from '../../../i18n';
import { error as logError } from '../../../lib/logger';
import { getErrorMessage } from '../../../lib/errors';

// Narrow type matching the specific Supabase select for part_request
// Note: orders uses !inner join which returns an array (one-to-many)
type PartRequestRow = {
  id: string;
  status: string;
  orders: Array<{
    id: string;
    customer_name: string;
    branches: Array<{
      organization_id: string;
    }>;
  }>;
};

interface AlertItem {
  id: string;
  order_id: string;
  type: 'delayed' | 'parts' | 'payment' | 'custom';
  message: string;
  created_at: string;
}

type AlertType = 'all' | 'delayed' | 'parts' | 'payment' | 'custom';

const ALERT_TYPE_TABS: { id: AlertType; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'delayed', label: 'Delayed' },
  { id: 'parts', label: 'Low Stock' },
  { id: 'payment', label: 'Payment' },
  { id: 'custom', label: 'Custom' },
];

export default function AlertsList() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const organizationId = useAuthStore(s => s.user?.organizationId);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<AlertType>('all');

  useEffect(() => {
    if (!organizationId) return;
    async function fetchAlerts() {
      try {
        // Get delayed orders (past estimated_date, not completed)
        const { data: delayed } = await supabase
          .from('orders')
          .select('id, customer_name, estimated_date')
          .eq('branches.organization_id', organizationId)
          .is('completed_at', null)
          .lt('estimated_date', new Date().toISOString())
          .limit(10);

        // Get pending parts requests
        const { data: parts } = await supabase
          .from('part_request')
          .select('id, status, orders!inner(id, customer_name, branches!inner(organization_id))')
          .eq('orders.branches.organization_id', organizationId)
          .eq('status', 'pending')
          .limit(10);

        const items: AlertItem[] = [];
        
        (delayed || []).forEach(o => {
          items.push({
            id: `delayed-${o.id}`,
            order_id: o.id,
            type: 'delayed',
            message: `Delayed delivery: ${o.customer_name}`,
            created_at: o.estimated_date || new Date().toISOString(),
          });
        });
        
        (parts || []).forEach(p => {
          const order = (p as PartRequestRow).orders[0];
          items.push({
            id: `parts-${p.id}`,
            order_id: order?.id || p.id,
            type: 'parts',
            message: `Parts request pending: ${order?.customer_name || 'Unknown'}`,
            created_at: new Date().toISOString(),
          });
        });

        setAlerts(items);
      } catch (err) {
        logError('Error fetching alerts', { error: getErrorMessage(err) });
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    }
    fetchAlerts();
  }, [organizationId]);

  const filteredAlerts = activeFilter === 'all' 
    ? alerts 
    : alerts.filter(a => a.type === activeFilter);

  const handleAlertClick = (alert: AlertItem) => {
    navigate(`/${lang}/admin/orders/${alert.order_id}`);
  };

  const getAlertIcon = (type: AlertItem['type']) => {
    switch (type) {
      case 'delayed':
        return '⚠️';
      case 'parts':
        return '🔧';
      case 'payment':
        return '💳';
      default:
        return '📋';
    }
  };

  const getAlertColor = (type: AlertItem['type']) => {
    switch (type) {
      case 'delayed':
        return '#D64545'; // red
      case 'parts':
        return '#eab308'; // yellow
      case 'payment':
        return '#000000'; // black
      default:
        return '#6b7280'; // gray
    }
  };

  return (
    <>
      <h1 className="text-2xl font-bold text-black font-nb mb-6">{t('alerts.title') || 'Alerts'}</h1>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {ALERT_TYPE_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveFilter(tab.id)}
            className={`px-4 py-2 text-sm font-nb font-medium border-3 border-nb-black rounded-[2px] transition-colors ${
              activeFilter === tab.id
                ? 'bg-nb-black text-white'
                : 'bg-white text-nb-black hover:bg-nb-yellow'
            }`}
          >
            {tab.label}
            {tab.id !== 'all' && (
              <span className="ml-2 text-xs opacity-75">
                ({alerts.filter(a => a.type === tab.id).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-nb-text-muted">Loading...</p>
      ) : filteredAlerts.length === 0 ? (
        <CardNB className="text-center" style={{ padding: '2rem', textAlign: 'center' }}>
          <p className="text-nb-text-muted">No active alerts</p>
        </CardNB>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredAlerts.map(alert => (
            <CardNB 
              key={alert.id} 
              variant="interactive" 
              style={{ padding: '1rem 1.5rem' }}
              onClick={() => handleAlertClick(alert)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '0.5rem', 
                  height: '0.5rem', 
                  borderRadius: '0',
                  background: getAlertColor(alert.type),
                }} />
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#000' }}>{alert.message}</p>
                  <p style={{ fontSize: '0.75rem', color: '#787878' }}>
                    {getAlertIcon(alert.type)} {alert.type === 'delayed' ? '⚠️ Delayed' : alert.type === 'parts' ? '🔧 Parts pending' : alert.type}
                  </p>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#787878' }}>
                  {new Date(alert.created_at).toLocaleDateString()}
                </span>
              </div>
            </CardNB>
          ))}
        </div>
      )}
    </>
  );
}
